import { useEffect, useMemo } from 'react'
import { useAppStore } from '../../../store/app'
import {
  sendCompactRules,
  sendDeleteRule,
  sendListRules,
} from '../../../api/ws'
import { SwipeableRow } from '../../../components/SwipeableRow'
import { groupRules, type RuleGroup } from './ruleGroups'
import './GrantsSegment.css'

export function GrantsSegment({ positionID }: { positionID: string }) {
  const rules = useAppStore((s) => s.rules)
  const groups = useMemo(() => {
    const scoped = rules.filter(
      (r) =>
        r.principal_scope_kind === 'position' &&
        r.principal_scope_value === positionID,
    )
    return groupRules(scoped)
  }, [rules, positionID])

  // Match native PositionGrantsView.onAppear: the server only pushes
  // rules_list on change events (creation/deletion/compact). Without an
  // explicit request, a freshly-opened segment shows empty even when
  // rules exist.
  useEffect(() => {
    sendListRules()
  }, [positionID])

  if (groups.length === 0) {
    return (
      <div className="grants__empty">
        <div className="grants__empty-icon" aria-hidden>
          ✓
        </div>
        <div className="grants__empty-title">No saved grants</div>
        <div className="grants__empty-body">
          Tap "Always Allow" on a permission request to save a rule here.
        </div>
      </div>
    )
  }

  function compact(group: RuleGroup) {
    if (!group.compactPattern) return
    sendCompactRules(positionID, group.compactPattern)
  }

  function deleteAll(group: RuleGroup) {
    for (const r of group.rules) sendDeleteRule(r.id)
  }

  return (
    <div className="grants">
      <div className="grants__list">
        {groups.map((g, i) => (
          <GroupRow
            key={i}
            group={g}
            onCompact={() => compact(g)}
            onDeleteAll={() => deleteAll(g)}
          />
        ))}
      </div>
    </div>
  )
}

function GroupRow({
  group,
  onCompact,
  onDeleteAll,
}: {
  group: RuleGroup
  onCompact: () => void
  onDeleteAll: () => void
}) {
  const isGroup = group.rules.length > 1
  if (!isGroup) {
    const r = group.rules[0]
    return (
      <SwipeableRow
        trailing={{ label: 'Delete', onAction: () => sendDeleteRule(r.id) }}
      >
        <div className="grants__row">
          <div className="grants__row-pattern">{r.predicate ?? r.action}</div>
          <div className="grants__row-meta">
            {r.action}
            {r.expires_at && (
              <>
                {' · '}
                <span className="grants__row-expires">
                  expires {formatRelative(r.expires_at)}
                </span>
              </>
            )}
          </div>
        </div>
      </SwipeableRow>
    )
  }

  // Multi-rule group: show a summary and expose Compact + Delete All.
  return (
    <SwipeableRow trailing={{ label: 'Delete All', onAction: onDeleteAll }}>
      <div className="grants__row">
        <div className="grants__row-pattern">
          {abbreviate(group.commonPrefix ?? '')}
          <span className="grants__row-ellipsis">…</span>
        </div>
        <div className="grants__row-meta">
          {group.tool} · {group.rules.length} rules
        </div>
        {group.compactPattern && (
          <button
            className="grants__compact"
            onClick={onCompact}
            aria-label="Compact into one pattern"
          >
            ⇢ Compact
          </button>
        )}
      </div>
    </SwipeableRow>
  )
}

/// Trim long prefixes to the last few path components so the row stays
/// readable. "a/b/c/d/e/f/g/" → "…/e/f/g/".
function abbreviate(prefix: string): string {
  const parts = prefix.split('/').filter((p) => p.length > 0)
  if (parts.length <= 3) return prefix
  return '…/' + parts.slice(-3).join('/') + '/'
}

function formatRelative(iso: string): string {
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return iso
  const diff = t - Date.now()
  const abs = Math.abs(diff)
  const minutes = Math.round(abs / 60_000)
  const hours = Math.round(abs / 3_600_000)
  const days = Math.round(abs / 86_400_000)
  const sign = diff < 0 ? 'ago' : 'in'
  if (minutes < 90) return `${sign} ${minutes}m`
  if (hours < 48) return `${sign} ${hours}h`
  return `${sign} ${days}d`
}
